import { useCallback, useSyncExternalStore } from 'react';

/**
 * Live counts for rail items that declare a `badgeKey`. A remote writes, the shell's rail reads:
 * @clarix/layout is an MF singleton, so this module-level map is the same instance in both.
 */

const counts = new Map<string, number>();
const listeners = new Set<() => void>();

/** Sets the badge for `key`; null, zero, negative or non-finite clears it. */
export function setRailBadge(key: string, count: number | null): void {
  const next = count != null && Number.isFinite(count) && count >= 1 ? Math.floor(count) : null;
  if ((counts.get(key) ?? null) === next) return;
  if (next == null) counts.delete(key);
  else counts.set(key, next);
  listeners.forEach((l) => l());
}

export function getRailBadge(key: string): number | null {
  return counts.get(key) ?? null;
}

export function subscribeRailBadges(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The live count for `key`, or null when there is none (or no key). */
export function useRailBadge(key: string | undefined): number | null {
  const getSnapshot = useCallback(() => (key ? getRailBadge(key) : null), [key]);
  return useSyncExternalStore(subscribeRailBadges, getSnapshot, getSnapshot);
}
